import type { App } from 'vue';
import type { Router } from 'vue-router';

const gtmEvents: string[] = [
    'content-view',
    'leadFormLogin',
];

const metrikaEvents: string[] = [
    'authLoginPageView',
    'authLoginEnter',
];

export interface AnalyticsOptions {
    gtmId?: string;
    metrikaId?: string;
    vueRouter?: Router;
}

let analyticsPlugin: AnalyticsPlugin;

export function useAnalytics(): AnalyticsPlugin {
    return analyticsPlugin;
}

export const Analytics = {
    install(app: App, options: AnalyticsOptions) {
        const allowedEvents: string[] = [];

        if (options.gtmId) {
            allowedEvents.push(...gtmEvents);
        }

        if (options.metrikaId) {
            allowedEvents.push(...metrikaEvents);
        }

        analyticsPlugin = new AnalyticsPlugin(allowedEvents);

        window.dataLayer = [];

        if (options.vueRouter) {
            initVueRouterGuard(options.vueRouter);
        }

        if (options.gtmId) {
            loadGtmScript(options.gtmId);
        }

        if (options.metrikaId) {
            loadMetrikaScript(options.metrikaId);
        }
    },
};

function initVueRouterGuard(vueRouter: Router): void {
    vueRouter.afterEach(async (to) => {
        if (typeof to.name !== 'string') {
            return;
        }

        const name: string = typeof to.meta.gtm === 'string' && !!to.meta.gtm
            ? to.meta.gtm
            : to.name;

        let fullUrl: string = vueRouter.options.history.base;
        if (!fullUrl.endsWith('/')) {
            fullUrl += '/';
        }

        fullUrl += to.fullPath.startsWith('/')
            ? to.fullPath.substring(1)
            : to.fullPath;

        analyticsPlugin.trackView(name, fullUrl);
    });
}

function loadGtmScript(id: string): void {
    const script: HTMLScriptElement = document.createElement('script');

    window.dataLayer.push({
        event: 'gtm.js',
        'gtm.start': new Date().getTime(),
    });

    script.async = true;

    const queryString: URLSearchParams = new URLSearchParams({ id });
    script.src = `https://www.googletagmanager.com/gtm.js?${queryString}`;

    document.body.appendChild(script);
}

function loadMetrikaScript(id: string): void {
    window.ym = window.ym || function(...args: unknown[]) {
        (window.ym.a = window.ym.a || []).push(args);
    };
    window.ym.l = Date.now();

    window.ym(Number(id), 'init', {
        clickmap: true,
        trackLinks: true,
        accurateTrackBounce: true,
        webvisor: true,
        trackHash: true,
    });

    const script: HTMLScriptElement = document.createElement('script');
    script.async = true;
    script.src = 'https://mc.yandex.ru/metrika/tag.js';

    document.body.appendChild(script);
}

class AnalyticsPlugin {
    private readonly allowedEvents: string[];

    constructor(allowedEvents: string[]) {
        this.allowedEvents = allowedEvents;
    }

    public trackView(screenName: string, path: string): void {
        if (!this.allowedEvents.includes('content-view')) {
            return;
        }

        window.dataLayer.push({
            event: 'content-view',
            'content-view-name': screenName,
            'content-name': path,
        });
    }

    public trackEvent(event: string): void {
        if (!this.allowedEvents.includes(event)) {
            return;
        }

        window.dataLayer.push({
            event: event,
        });
    }
}
